// src/pages/PolicyPage.js
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../styles/PolicyPage.css';
import MarketingLayout from '../components/marketing/MarketingLayout';
import useDocumentMeta from '../hooks/useDocumentMeta';

const privacyPolicyContent = `
  <h2>Privacy Policy</h2>
  <p><strong>Last Updated:</strong> 27 September 2026</p>
  
  <h3>1. Introduction and Data Controller</h3>
  <p>VELOX Network ("we", "our", "us", or "Company") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, process, share, and protect your information when you use our digital business card platform and related services ("Services").</p>
  <p><strong>Data Controller:</strong> VELOX Network<br/>
  <strong>Contact:</strong> support@velox-network.com<br/>
  <strong>Data Protection Officer:</strong> dpo@velox-network.com</p>
  
  <h3>2. Legal Basis for Processing</h3>
  <p>We process your personal data based on the following legal grounds under GDPR Article 6:</p>
  <ul>
    <li><strong>Contract Performance (Article 6(1)(b)):</strong> Account creation, service delivery, business card functionality, contact management, email communications, order processing</li>
    <li><strong>Legitimate Interest (Article 6(1)(f)):</strong> Security monitoring, fraud prevention, service improvement, analytics (anonymized), customer support</li>
    <li><strong>Consent (Article 6(1)(a)):</strong> Marketing communications, location tracking, optional analytics, third-party integrations, cookies (non-essential)</li>
    <li><strong>Legal Obligation (Article 6(1)(c)):</strong> Compliance with applicable laws, tax obligations, data breach notifications</li>
  </ul>
  
  <h3>3. Data We Collect</h3>
  <p>We collect the following categories of personal data:</p>
  
  <h4>3.1 Account and Profile Data</h4>
  <ul>
    <li><strong>Identity Data:</strong> Full name, email address, phone number, date of birth (optional; not used to confirm your age and not shown on your card), title</li>
    <li><strong>Professional Data:</strong> Company name, job title/role, business address, professional social media profiles (LinkedIn, etc.)</li>
    <li><strong>Profile Content:</strong> Profile pictures, business card designs, custom URLs, social media links</li>
    <li><strong>Authentication Data:</strong> Password (hashed), security questions, two-factor authentication settings</li>
  </ul>
  
  <h4>3.2 Contact and Networking Data</h4>
  <ul>
    <li><strong>Contact Information:</strong> Details of contacts you save, business cards you share/receive</li>
    <li><strong>Interaction Data:</strong> Connection timestamps, meeting locations, contact notes, relationship categories</li>
    <li><strong>Communication Records:</strong> Email invitations sent/received, message history, notification preferences</li>
  </ul>
  
  <h4>3.3 Location and Device Data</h4>
  <ul>
    <li><strong>Location Data:</strong> GPS coordinates when making connections (with explicit consent), IP address geolocation</li>
    <li><strong>Device Information:</strong> Device type, operating system, browser type, unique device identifiers</li>
    <li><strong>NFC Data:</strong> NFC card identifiers, activation codes, card linking information</li>
  </ul>
  
  <h4>3.4 Usage and Analytics Data</h4>
  <ul>
    <li><strong>Service Usage:</strong> Features used, session duration, click patterns, search queries</li>
    <li><strong>Performance Data:</strong> Error logs, crash reports, response times, system performance metrics</li>
    <li><strong>Business Analytics:</strong> Networking patterns, contact growth, engagement metrics, ROI tracking</li>
  </ul>
  
  <h4>3.5 Commercial and Transaction Data</h4>
  <ul>
    <li><strong>Order Information:</strong> Purchase history, payment methods, billing addresses, order fulfillment</li>
    <li><strong>Subscription Data:</strong> Plan types, billing cycles, usage limits, feature access</li>
    <li><strong>Support Data:</strong> Customer service interactions, support tickets, feedback submissions</li>
  </ul>
  
  <h3>4. How We Use Your Data</h3>
  <p>We process your personal data for the following purposes:</p>
  
  <h4>4.1 Service Provision and Management</h4>
  <ul>
    <li>Creating and managing your account and profile</li>
    <li>Enabling digital business card creation, sharing, and management</li>
    <li>Facilitating contact exchange and networking functionality</li>
    <li>Processing NFC card activations and linking</li>
    <li>Providing location-based networking features</li>
    <li>Managing user preferences and notification settings</li>
  </ul>
  
  <h4>4.2 Communication and Support</h4>
  <ul>
    <li>Sending transactional emails (account verification, password resets, order confirmations)</li>
    <li>Delivering business card invitations and networking notifications</li>
    <li>Providing customer support and technical assistance</li>
    <li>Sending service updates and important announcements</li>
    <li>Marketing communications (with explicit consent only)</li>
  </ul>
  
  <h4>4.3 Security and Fraud Prevention</h4>
  <ul>
    <li>Monitoring for suspicious activities and security threats</li>
    <li>Preventing fraud, abuse, and unauthorized access</li>
    <li>Maintaining audit logs for security and compliance</li>
    <li>Implementing access controls and authentication measures</li>
  </ul>
  
  <h4>4.4 Analytics and Improvement</h4>
  <ul>
    <li>Analyzing usage patterns to improve our Services</li>
    <li>Developing new features and functionality</li>
    <li>Conducting research and development activities</li>
    <li>Measuring service performance and user satisfaction</li>
  </ul>
  
  <h4>4.5 Legal and Compliance</h4>
  <ul>
    <li>Complying with legal obligations and regulatory requirements</li>
    <li>Responding to legal requests and court orders</li>
    <li>Protecting our rights and interests in legal proceedings</li>
    <li>Ensuring compliance with terms of service and policies</li>
  </ul>
  
  <h3>5. Data Sharing and Disclosure</h3>
  <p>We may share your personal data in the following circumstances:</p>
  
  <h4>5.1 Service Providers and Partners</h4>
  <ul>
    <li><strong>Cloud Infrastructure:</strong> AWS (hosting, storage, computing)</li>
    <li><strong>Database Services:</strong> MongoDB Atlas (data storage and management)</li>
    <li><strong>Email Services:</strong> AWS SES (email delivery and management)</li>
    <li><strong>Payment Processors:</strong> Shopify, Stripe (payment processing)</li>
    <li><strong>Analytics Providers:</strong> Google Analytics, custom analytics (with consent)</li>
    <li><strong>Customer Support:</strong> Support ticket systems and communication tools</li>
  </ul>
  
  <h4>5.2 Business Transfers</h4>
  <p>In the event of a merger, acquisition, sale of assets, or bankruptcy, your personal data may be transferred to the acquiring entity, subject to the same privacy protections.</p>
  
  <h4>5.3 Legal Requirements</h4>
  <p>We may disclose your data when required by law, court order, or to protect our rights, safety, or the rights and safety of others.</p>
  
  <h4>5.4 Consent-Based Sharing</h4>
  <p>We may share data with third parties when you provide explicit consent, such as integrations with CRM systems, calendar applications, or social media platforms.</p>
  
  <h3>6. International Data Transfers</h3>
  <p>Your data is primarily processed within the European Economic Area (EEA) using AWS eu-west-2 (London) region. When data is transferred outside the EEA, we ensure adequate protection through:</p>
  <ul>
    <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
    <li>Adequacy decisions for countries with equivalent data protection</li>
    <li>Binding Corporate Rules (BCRs) where applicable</li>
    <li>Explicit consent for specific transfers when required</li>
  </ul>
  
  <h3>7. Your Rights Under GDPR</h3>
  <p>As a data subject, you have the following rights:</p>
  
  <h4>7.1 Right of Access (Article 15)</h4>
  <p>You can request access to your personal data and information about how we process it. Access your data through your account settings or contact us at support@velox-network.com.</p>
  
  <h4>7.2 Right to Rectification (Article 16)</h4>
  <p>You can correct inaccurate or incomplete personal data through your account settings or by contacting us.</p>
  
  <h4>7.3 Right to Erasure (Article 17)</h4>
  <p>You can request deletion of your personal data. Use the "Delete Account" feature in settings or contact us. Note that some data may be retained for legal obligations.</p>
  
  <h4>7.4 Right to Restrict Processing (Article 18)</h4>
  <p>You can request restriction of processing in certain circumstances, such as when contesting data accuracy or objecting to processing.</p>
  
  <h4>7.5 Right to Data Portability (Article 20)</h4>
  <p>You can request your data in a structured, machine-readable format. Contact support@velox-network.com for data export.</p>
  
  <h4>7.6 Right to Object (Article 21)</h4>
  <p>You can object to processing based on legitimate interests or for direct marketing purposes. Manage preferences in your account settings.</p>
  
  <h4>7.7 Rights Related to Automated Decision-Making (Article 22)</h4>
  <p>You have rights regarding automated decision-making and profiling. We will implement AI features with appropriate safeguards and human oversight.</p>
  
  <h4>7.8 Right to Withdraw Consent</h4>
  <p>You can withdraw consent at any time through your account settings. This does not affect the lawfulness of processing based on consent before withdrawal.</p>
  
  <h3>8. Data Retention</h3>
  <p>We retain personal data for the following periods:</p>
  <ul>
    <li><strong>Account Data:</strong> Until account deletion plus 30 days for backup recovery</li>
    <li><strong>Contact Data:</strong> Until account deletion or individual contact removal</li>
    <li><strong>Transaction Data:</strong> 7 years for tax and legal compliance</li>
    <li><strong>Marketing Data:</strong> Until consent withdrawal plus 30 days</li>
    <li><strong>Security Logs:</strong> 2 years for security monitoring and incident response</li>
    <li><strong>Analytics Data:</strong> 26 months in anonymized form</li>
  </ul>
  
  <h3>9. Security Measures</h3>
  <p>We implement comprehensive security measures including:</p>
  <ul>
    <li><strong>Encryption:</strong> TLS 1.3 for data in transit, AES-256 for data at rest</li>
    <li><strong>Access Controls:</strong> Multi-factor authentication, role-based access, principle of least privilege</li>
    <li><strong>Infrastructure Security:</strong> AWS security controls, regular security assessments, penetration testing</li>
    <li><strong>Application Security:</strong> Input validation, SQL injection prevention, XSS protection</li>
    <li><strong>Monitoring:</strong> 24/7 security monitoring, intrusion detection, audit logging</li>
    <li><strong>Incident Response:</strong> Documented procedures for security incidents and data breaches</li>
  </ul>
  
  <h3>10. Cookies and Tracking Technologies</h3>
  <p>We use cookies and similar technologies for:</p>
  <ul>
    <li><strong>Essential Cookies:</strong> Authentication, security, basic functionality (no consent required)</li>
    <li><strong>Analytics Cookies:</strong> Usage statistics, performance monitoring (consent required)</li>
    <li><strong>Preference Cookies:</strong> User settings, language preferences (consent required)</li>
    <li><strong>Marketing Cookies:</strong> Advertising, remarketing (explicit consent required)</li>
  </ul>
  <p>Manage cookie preferences through your browser settings or our cookie consent banner.</p>
  
  <h3>11. Children's Privacy</h3>
  <p>Our Services are not intended for children under 16 years of age. We do not knowingly collect personal data from children under 16. If you are under 16, please do not use our Services or provide any personal information. If we become aware that we have collected personal data from a child under 16, we will delete such information immediately.</p>
  
  <h3>12. Future Features and Consent</h3>
  <p>As we develop new features, we may process additional data types including:</p>
  <ul>
    <li><strong>AI and Machine Learning:</strong> Networking recommendations, contact scoring, predictive analytics</li>
    <li><strong>Advanced Integrations:</strong> CRM systems, calendar applications, video conferencing platforms</li>
    <li><strong>Biometric Data:</strong> Voice recordings for voice commands (with explicit consent)</li>
    <li><strong>Video Content:</strong> Video business cards, meeting recordings (with explicit consent)</li>
    <li><strong>IoT Integration:</strong> Smart office systems, event management platforms</li>
    <li><strong>Blockchain Data:</strong> Identity verification, transaction records (with explicit consent)</li>
  </ul>
  <p>We will obtain appropriate consent and update this policy before implementing features that require additional data processing.</p>
  
  <h3>13. Data Breach Notification</h3>
  <p>In the event of a data breach that poses a high risk to your rights and freedoms, we will notify you within 72 hours of becoming aware of the breach. Notifications will include:</p>
  <ul>
    <li>Nature of the breach and data categories affected</li>
    <li>Likely consequences of the breach</li>
    <li>Measures taken to address the breach</li>
    <li>Recommendations for protecting yourself</li>
  </ul>
  
  <h3>14. Supervisory Authority</h3>
  <p>You have the right to lodge a complaint with a supervisory authority if you believe we have violated your data protection rights. The relevant authorities include:</p>
  <ul>
    <li><strong>UK:</strong> Information Commissioner's Office (ICO) - ico.org.uk</li>
    <li><strong>EU:</strong> Your local Data Protection Authority</li>
  </ul>
  
  <h3>15. Changes to This Privacy Policy</h3>
  <p>We may update this Privacy Policy to reflect changes in our practices, technology, legal requirements, or other factors. We will:</p>
  <ul>
    <li>Notify you of material changes via email or in-app notification</li>
    <li>Post the updated policy with a new "Last Updated" date</li>
    <li>Obtain new consent where required by law</li>
    <li>Provide a summary of key changes when significant updates are made</li>
  </ul>
  
  <h3>16. Contact Information</h3>
  <p>For questions, concerns, or to exercise your rights regarding this Privacy Policy or your personal data, contact us:</p>
  <ul>
    <li><strong>Email:</strong> support@velox-network.com</li>
    <li><strong>Data Protection Officer:</strong> dpo@velox-network.com</li>
    <li><strong>Subject Line:</strong> "Privacy Policy Inquiry" or "Data Subject Rights Request"</li>
  </ul>
  <p>We will respond to your inquiry within 30 days (or 1 month under GDPR).</p>
`;

const termsOfServiceContent = `
  <h2>Terms of Service</h2>
  <p><strong>Last Updated:</strong> 27 September 2026</p>
  
  <h3>1. Agreement and Acceptance</h3>
  <p>These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and VELOX Network ("Company", "we", "our", or "us"). By accessing, downloading, installing, or using our digital business card platform and related services ("Services"), you agree to be bound by these Terms and our Privacy Policy.</p>
  <p>If you do not agree to these Terms, you must not use our Services.</p>
  
  <h3>2. Eligibility and Account Requirements</h3>
  <h4>2.1 Age Requirements</h4>
  <p>You must be at least 16 years old to create an account. By creating an account you confirm that you are at least 16. If you are between 16 and 18 years old, you represent that you have parental or guardian consent to use our Services.</p>
  
  <h4>2.2 Account Registration</h4>
  <p>To use our Services, you must:</p>
  <ul>
    <li>Provide accurate, current, and complete information during registration</li>
    <li>Maintain and update your account information</li>
    <li>Keep your login credentials secure and confidential</li>
    <li>Notify us immediately of any unauthorized use of your account</li>
    <li>Accept responsibility for all activities under your account</li>
  </ul>
  
  <h4>2.3 Account Verification</h4>
  <p>We may require email verification, phone verification, or other identity verification measures to activate your account and access certain features.</p>
  
  <h3>3. Description of Services</h3>
  <p>VELOX Network provides a comprehensive digital business card platform including:</p>
  <ul>
    <li>Digital business card creation, customization, and sharing</li>
    <li>NFC-enabled physical card integration and activation</li>
    <li>Contact management and networking tools</li>
    <li>Location-based networking features and analytics</li>
    <li>Email communication and invitation systems</li>
    <li>Analytics and insights dashboards</li>
    <li>Integration with third-party services and platforms</li>
    <li>Mobile and web application access</li>
    <li>Customer support and account management</li>
  </ul>
  
  <h3>4. User License and Restrictions</h3>
  <h4>4.1 License Grant</h4>
  <p>Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use our Services for your personal or business networking purposes.</p>
  
  <h4>4.2 Restrictions</h4>
  <p>You agree not to:</p>
  <ul>
    <li>Use our Services for any illegal, harmful, or unauthorized purposes</li>
    <li>Violate any applicable laws, regulations, or third-party rights</li>
    <li>Reverse engineer, decompile, or attempt to extract source code</li>
    <li>Interfere with or disrupt our Services or servers</li>
    <li>Use automated systems to access our Services without permission</li>
    <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
    <li>Upload malicious code, viruses, or harmful content</li>
    <li>Spam, harass, or abuse other users</li>
    <li>Impersonate others or provide false information</li>
    <li>Use our Services to compete with us or develop competing products</li>
  </ul>
  
  <h3>5. Content and Intellectual Property</h3>
  <h4>5.1 Your Content</h4>
  <p>You retain ownership of all content you create, upload, or share through our Services ("User Content"), including:</p>
  <ul>
    <li>Business card information and designs</li>
    <li>Profile pictures and media</li>
    <li>Contact information and notes</li>
    <li>Messages and communications</li>
  </ul>
  
  <h4>5.2 License to Your Content</h4>
  <p>By using our Services, you grant us a worldwide, non-exclusive, royalty-free license to:</p>
  <ul>
    <li>Host, store, and display your User Content</li>
    <li>Process and transmit your User Content to provide our Services</li>
    <li>Create backups and ensure service reliability</li>
    <li>Comply with legal obligations and enforce these Terms</li>
  </ul>
  <p>This license terminates when you delete your content or account, except for content shared with other users.</p>
  
  <h4>5.3 Our Intellectual Property</h4>
  <p>Our Services, including software, designs, trademarks, and proprietary technology, are owned by VELOX Network and protected by intellectual property laws. You may not use our intellectual property without written permission.</p>
  
  <h3>6. Privacy and Data Protection</h3>
  <p>Your privacy is important to us. Our collection, use, and protection of your personal data is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our Services, you consent to our data practices as described in the Privacy Policy.</p>
  
  <h4>6.1 Location Data</h4>
  <p>Our Services may collect location data when you make networking connections. You can control location sharing through your device settings and account preferences. Disabling location sharing may limit certain features.</p>
  
  <h4>6.2 Contact Data</h4>
  <p>When you share your business card or save contacts, you represent that you have the right to share such information and that recipients have consented to receive it.</p>
  
  <h3>7. Payment Terms and Subscriptions</h3>
  <h4>7.1 Pricing and Billing</h4>
  <p>Certain features may require payment of fees. Current pricing is available on our website and may change with notice. You agree to pay all applicable fees and taxes.</p>
  
  <h4>7.2 Subscriptions</h4>
  <p>Subscription services automatically renew unless cancelled. You may cancel at any time through your account settings. Cancellation takes effect at the end of the current billing period.</p>
  
  <h4>7.3 Refunds</h4>
  <p>Refunds are provided according to our refund policy. Digital products and services are generally non-refundable except as required by law.</p>
  
  <h4>7.4 Physical Products</h4>
  <p>Orders for NFC cards and physical products are processed through our e-commerce partners. Separate terms may apply to physical product orders.</p>
  
  <h3>8. Acceptable Use and Community Guidelines</h3>
  <p>You agree to use our Services responsibly and in accordance with these guidelines:</p>
  
  <h4>8.1 Professional Conduct</h4>
  <ul>
    <li>Use our Services for legitimate business networking purposes</li>
    <li>Provide accurate and truthful information in your profile</li>
    <li>Respect other users' privacy and preferences</li>
    <li>Maintain professional standards in communications</li>
  </ul>
  
  <h4>8.2 Prohibited Content</h4>
  <p>You may not upload, share, or transmit content that is:</p>
  <ul>
    <li>Illegal, harmful, threatening, or abusive</li>
    <li>Defamatory, libelous, or invasive of privacy</li>
    <li>Discriminatory or hateful based on protected characteristics</li>
    <li>Sexually explicit or inappropriate</li>
    <li>Infringing on intellectual property rights</li>
    <li>Spam, promotional, or commercial (except business card information)</li>
    <li>Misleading, fraudulent, or deceptive</li>
  </ul>
  
  <h3>9. Third-Party Integrations and Services</h3>
  <p>Our Services may integrate with third-party platforms and services, including:</p>
  <ul>
    <li>Social media platforms (LinkedIn, Twitter, etc.)</li>
    <li>CRM systems (Salesforce, HubSpot, etc.)</li>
    <li>Calendar applications (Google Calendar, Outlook, etc.)</li>
    <li>Email marketing platforms</li>
    <li>Video conferencing tools</li>
    <li>Payment processors and e-commerce platforms</li>
  </ul>
  <p>Third-party services are governed by their own terms and privacy policies. We are not responsible for third-party services or their practices.</p>
  
  <h3>10. Service Availability and Modifications</h3>
  <h4>10.1 Service Availability</h4>
  <p>We strive to provide reliable service but cannot guarantee 100% uptime. Our Services may be temporarily unavailable due to maintenance, updates, or technical issues.</p>
  
  <h4>10.2 Service Modifications</h4>
  <p>We may modify, update, or discontinue features of our Services at any time. We will provide reasonable notice of material changes that affect your use of the Services.</p>
  
  <h4>10.3 Beta Features</h4>
  <p>We may offer beta or experimental features that are provided "as is" without warranties. Beta features may be modified or discontinued without notice.</p>
  
  <h3>11. Account Termination and Suspension</h3>
  <h4>11.1 Termination by You</h4>
  <p>You may terminate your account at any time through your account settings or by contacting support@velox-network.com. Upon termination:</p>
  <ul>
    <li>Your access to our Services will cease</li>
    <li>Your data will be deleted according to our data retention policy</li>
    <li>Shared content may remain accessible to recipients</li>
    <li>Outstanding fees remain due and payable</li>
  </ul>
  
  <h4>11.2 Termination by Us</h4>
  <p>We may suspend or terminate your account if you:</p>
  <ul>
    <li>Violate these Terms or our policies</li>
    <li>Engage in fraudulent or illegal activities</li>
    <li>Fail to pay required fees</li>
    <li>Pose a security risk to our Services or other users</li>
  </ul>
  
  <h4>11.3 Effect of Termination</h4>
  <p>Upon termination, all rights and licenses granted to you will cease, and you must stop using our Services. Provisions that should survive termination will remain in effect.</p>
  
  <h3>12. Disclaimers and Limitation of Liability</h3>
  <h4>12.1 Service Disclaimers</h4>
  <p>Our Services are provided "as is" and "as available" without warranties of any kind. We disclaim all warranties, express or implied, including:</p>
  <ul>
    <li>Merchantability and fitness for a particular purpose</li>
    <li>Non-infringement of third-party rights</li>
    <li>Uninterrupted or error-free operation</li>
    <li>Accuracy or reliability of content</li>
    <li>Security of data transmission</li>
  </ul>
  
  <h4>12.2 Limitation of Liability</h4>
  <p>To the maximum extent permitted by law, our total liability for any claims arising from or related to our Services is limited to the greater of:</p>
  <ul>
    <li>€100 EUR, or</li>
    <li>The amount you paid us in the 12 months preceding the claim</li>
  </ul>
  
  <h4>12.3 Excluded Damages</h4>
  <p>We will not be liable for indirect, incidental, special, consequential, or punitive damages, including:</p>
  <ul>
    <li>Lost profits or business opportunities</li>
    <li>Data loss or corruption</li>
    <li>Business interruption</li>
    <li>Reputational harm</li>
  </ul>
  
  <h3>13. Indemnification</h3>
  <p>You agree to indemnify, defend, and hold harmless VELOX Network and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:</p>
  <ul>
    <li>Your use of our Services</li>
    <li>Your violation of these Terms</li>
    <li>Your User Content</li>
    <li>Your violation of third-party rights</li>
  </ul>
  
  <h3>14. Dispute Resolution</h3>
  <h4>14.1 Informal Resolution</h4>
  <p>Before initiating formal proceedings, you agree to attempt to resolve disputes informally by contacting support@velox-network.com. We will work in good faith to resolve disputes within 60 days.</p>
  
  <h4>14.2 Governing Law</h4>
  <p>These Terms are governed by the laws of England and Wales, without regard to conflict of law principles.</p>
  
  <h4>14.3 Jurisdiction</h4>
  <p>Any legal proceedings must be brought in the courts of England and Wales. You consent to the personal jurisdiction of these courts.</p>
  
  <h4>14.4 Arbitration (Optional)</h4>
  <p>For disputes involving claims under €10,000, either party may elect binding arbitration under the rules of the London Court of International Arbitration (LCIA).</p>
  
  <h3>15. Force Majeure</h3>
  <p>We will not be liable for delays or failures in performance due to circumstances beyond our reasonable control, including natural disasters, government actions, pandemics, cyber attacks, or infrastructure failures.</p>
  
  <h3>16. Severability and Waiver</h3>
  <p>If any provision of these Terms is found invalid or unenforceable, the remaining provisions will continue in effect. Our failure to enforce any provision does not constitute a waiver of our rights.</p>
  
  <h3>17. Assignment</h3>
  <p>We may assign these Terms and our rights and obligations to any party, including in connection with a merger, acquisition, or sale of assets. You may not assign your rights under these Terms without our written consent.</p>
  
  <h3>18. Entire Agreement</h3>
  <p>These Terms, together with our Privacy Policy and any additional terms for specific features, constitute the entire agreement between you and VELOX Network regarding our Services.</p>
  
  <h3>19. Updates to Terms</h3>
  <p>We may update these Terms from time to time to reflect changes in our Services, legal requirements, or business practices. We will:</p>
  <ul>
    <li>Notify you of material changes via email or in-app notification</li>
    <li>Post updated Terms with a new "Last Updated" date</li>
    <li>Provide a reasonable notice period before changes take effect</li>
    <li>Allow you to terminate your account if you disagree with changes</li>
  </ul>
  <p>Your continued use of our Services after changes take effect constitutes acceptance of the updated Terms.</p>
  
  <h3>20. Contact Information</h3>
  <p>For questions about these Terms or our Services, contact us:</p>
  <ul>
    <li><strong>Email:</strong> support@velox-network.com</li>
    <li><strong>Legal Inquiries:</strong> legal@velox-network.com</li>
    <li><strong>Subject Line:</strong> "Terms of Service Inquiry"</li>
  </ul>
  <p>We will respond to your inquiry within 5 business days.</p>
  
  <h3>21. Acknowledgment</h3>
  <p>By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. You represent that you have the authority to enter into this agreement and that your use of our Services complies with all applicable laws and regulations.</p>
`;

const cookiePolicyContent = `
  <h2>Cookie Policy</h2>
  <p><strong>Last Updated:</strong> December 2024</p>
  
  <h3>1. What Are Cookies</h3>
  <p>Cookies are small text files that are placed on your device (computer, smartphone, tablet) when you visit our website. They help us provide you with a better experience by remembering your preferences, keeping you logged in, and understanding how you use our Services.</p>
  
  <h3>2. How We Use Cookies</h3>
  <p>VELOX Network uses cookies for the following purposes:</p>
  
  <h4>2.1 Essential Cookies (Always Active)</h4>
  <p>These cookies are necessary for our website to function properly and cannot be disabled:</p>
  <ul>
    <li><strong>Authentication:</strong> Keep you logged in to your account</li>
    <li><strong>Security:</strong> Protect against fraud and unauthorized access</li>
    <li><strong>Session Management:</strong> Maintain your session across pages</li>
    <li><strong>Load Balancing:</strong> Distribute traffic across our servers</li>
  </ul>
  
  <h4>2.2 Functional Cookies (Optional)</h4>
  <p>These cookies enhance your experience but are not essential:</p>
  <ul>
    <li><strong>Language Preferences:</strong> Remember your language selection</li>
    <li><strong>Theme Settings:</strong> Save your dark/light mode preference</li>
    <li><strong>Form Data:</strong> Remember information you've entered in forms</li>
    <li><strong>Accessibility Settings:</strong> Store your accessibility preferences</li>
  </ul>
  
  <h4>2.3 Analytics Cookies (Optional)</h4>
  <p>These cookies help us understand how you use our website:</p>
  <ul>
    <li><strong>Usage Statistics:</strong> Track page views, session duration, bounce rates</li>
    <li><strong>Performance Monitoring:</strong> Identify slow-loading pages and errors</li>
    <li><strong>Feature Usage:</strong> Understand which features are most popular</li>
    <li><strong>User Journey:</strong> Analyze how users navigate through our site</li>
  </ul>
  
  <h4>2.4 Marketing Cookies (Optional)</h4>
  <p>These cookies are used for advertising and marketing purposes:</p>
  <ul>
    <li><strong>Targeted Advertising:</strong> Show relevant ads based on your interests</li>
    <li><strong>Conversion Tracking:</strong> Measure the effectiveness of our marketing campaigns</li>
    <li><strong>Social Media Integration:</strong> Enable sharing and social media features</li>
    <li><strong>Remarketing:</strong> Show ads to users who have visited our website</li>
  </ul>
  
  <h3>3. Types of Cookies We Use</h3>
  
  <h4>3.1 First-Party Cookies</h4>
  <p>These are cookies set directly by VELOX Network:</p>
  <table>
    <tr>
      <th>Cookie Name</th>
      <th>Purpose</th>
      <th>Duration</th>
      <th>Type</th>
    </tr>
    <tr>
      <td>velox_auth_token</td>
      <td>Authentication and session management</td>
      <td>7 days</td>
      <td>Essential</td>
    </tr>
    <tr>
      <td>velox_preferences</td>
      <td>Store user preferences and settings</td>
      <td>1 year</td>
      <td>Functional</td>
    </tr>
    <tr>
      <td>velox_analytics</td>
      <td>Track usage and performance</td>
      <td>2 years</td>
      <td>Analytics</td>
    </tr>
    <tr>
      <td>velox_consent</td>
      <td>Remember your cookie preferences</td>
      <td>1 year</td>
      <td>Essential</td>
    </tr>
  </table>
  
  <h4>3.2 Third-Party Cookies</h4>
  <p>These are cookies set by external services we use:</p>
  <ul>
    <li><strong>Google Analytics:</strong> _ga, _gid, _gat - Website analytics and performance tracking</li>
    <li><strong>Google Fonts:</strong> Font loading and display optimization</li>
    <li><strong>Cloudflare:</strong> __cflb, __cf_bm - Security and performance optimization</li>
    <li><strong>Social Media:</strong> Cookies from LinkedIn, Twitter when you interact with social features</li>
  </ul>
  
  <h3>4. Cookie Consent and Management</h3>
  
  <h4>4.1 Your Consent Choices</h4>
  <p>When you first visit our website, you'll see a cookie consent banner with the following options:</p>
  <ul>
    <li><strong>Accept All:</strong> Consent to all cookie categories</li>
    <li><strong>Reject All:</strong> Only essential cookies will be used</li>
    <li><strong>Customize Settings:</strong> Choose which cookie categories to allow</li>
  </ul>
  
  <h4>4.2 Managing Your Cookie Preferences</h4>
  <p>You can change your cookie preferences at any time by:</p>
  <ul>
    <li>Clicking the "Cookie Settings" link in our website footer</li>
    <li>Visiting your account settings page</li>
    <li>Using your browser's cookie management tools</li>
    <li>Contacting us at support@velox-network.com</li>
  </ul>
  
  <h4>4.3 Browser Cookie Controls</h4>
  <p>Most browsers allow you to control cookies through their settings:</p>
  <ul>
    <li><strong>Chrome:</strong> Settings > Privacy and Security > Cookies and other site data</li>
    <li><strong>Firefox:</strong> Settings > Privacy & Security > Cookies and Site Data</li>
    <li><strong>Safari:</strong> Preferences > Privacy > Manage Website Data</li>
    <li><strong>Edge:</strong> Settings > Cookies and site permissions > Cookies and site data</li>
  </ul>
  
  <h3>5. Impact of Disabling Cookies</h3>
  <p>If you disable certain cookies, some features may not work properly:</p>
  
  <h4>5.1 Essential Cookies Disabled</h4>
  <ul>
    <li>You may not be able to log in or stay logged in</li>
    <li>Security features may not function correctly</li>
    <li>Some pages may not load properly</li>
  </ul>
  
  <h4>5.2 Functional Cookies Disabled</h4>
  <ul>
    <li>Your preferences won't be remembered between visits</li>
    <li>You'll need to re-enter information in forms</li>
    <li>Language and theme settings won't persist</li>
  </ul>
  
  <h4>5.3 Analytics Cookies Disabled</h4>
  <ul>
    <li>We won't be able to improve our website based on usage data</li>
    <li>Performance issues may take longer to identify and fix</li>
    <li>New features may not be optimized for user needs</li>
  </ul>
  
  <h4>5.4 Marketing Cookies Disabled</h4>
  <ul>
    <li>You may see less relevant advertisements</li>
    <li>Social media features may not work properly</li>
    <li>We won't be able to measure marketing campaign effectiveness</li>
  </ul>
  
  <h3>6. Contact Information</h3>
  <p>If you have questions about our use of cookies or this Cookie Policy, please contact us:</p>
  <ul>
    <li><strong>Email:</strong> support@velox-network.com</li>
    <li><strong>Data Protection Officer:</strong> dpo@velox-network.com</li>
    <li><strong>Subject Line:</strong> "Cookie Policy Inquiry"</li>
    <li><strong>Response Time:</strong> We will respond within 30 days</li>
  </ul>
`;

const accessibilityContent = `
  <h2>Accessibility Statement</h2>
  <p><strong>Last Updated:</strong> December 2024</p>
  
  <h3>1. Our Commitment to Accessibility</h3>
  <p>VELOX Network is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all users.</p>
  
  <h3>2. Accessibility Standards</h3>
  <p>We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. These guidelines explain how to make web content more accessible for people with disabilities and user-friendly for everyone.</p>
  
  <h4>2.1 WCAG 2.1 Compliance</h4>
  <p>Our website aims to achieve compliance with the following WCAG 2.1 Level AA criteria:</p>
  <ul>
    <li><strong>Perceivable:</strong> Information and user interface components must be presentable to users in ways they can perceive</li>
    <li><strong>Operable:</strong> User interface components and navigation must be operable</li>
    <li><strong>Understandable:</strong> Information and the operation of user interface must be understandable</li>
    <li><strong>Robust:</strong> Content must be robust enough that it can be interpreted reliably by a wide variety of user agents, including assistive technologies</li>
  </ul>
  
  <h3>3. Accessibility Features</h3>
  
  <h4>3.1 Visual Accessibility</h4>
  <ul>
    <li><strong>High Contrast Mode:</strong> Dark and light theme options for better visibility</li>
    <li><strong>Scalable Text:</strong> Text can be resized up to 200% without loss of functionality</li>
    <li><strong>Color Contrast:</strong> All text meets WCAG AA color contrast ratios (4.5:1 for normal text, 3:1 for large text)</li>
    <li><strong>Focus Indicators:</strong> Clear visual focus indicators for keyboard navigation</li>
    <li><strong>Alternative Text:</strong> Descriptive alt text for all images and icons</li>
    <li><strong>Consistent Layout:</strong> Predictable and consistent page layouts and navigation</li>
  </ul>
  
  <h4>3.2 Motor Accessibility</h4>
  <ul>
    <li><strong>Keyboard Navigation:</strong> Full functionality available via keyboard</li>
    <li><strong>Large Click Targets:</strong> Buttons and links are at least 44x44 pixels</li>
    <li><strong>No Time Limits:</strong> No automatic timeouts that cannot be extended</li>
    <li><strong>Skip Links:</strong> Skip to main content links for efficient navigation</li>
    <li><strong>Drag and Drop Alternatives:</strong> Alternative methods for drag-and-drop interactions</li>
  </ul>
  
  <h4>3.3 Cognitive Accessibility</h4>
  <ul>
    <li><strong>Clear Language:</strong> Simple, clear language and instructions</li>
    <li><strong>Error Prevention:</strong> Clear error messages and correction suggestions</li>
    <li><strong>Consistent Navigation:</strong> Navigation elements appear in the same location on each page</li>
    <li><strong>Help Documentation:</strong> Context-sensitive help and documentation</li>
    <li><strong>Progress Indicators:</strong> Clear indication of progress through multi-step processes</li>
  </ul>
  
  <h4>3.4 Hearing Accessibility</h4>
  <ul>
    <li><strong>Visual Alerts:</strong> Visual indicators for audio alerts and notifications</li>
    <li><strong>Captions:</strong> Captions provided for any video content</li>
    <li><strong>Transcripts:</strong> Text transcripts for audio content</li>
    <li><strong>No Auto-Play:</strong> Audio and video content does not auto-play</li>
  </ul>
  
  <h3>4. Assistive Technology Support</h3>
  
  <h4>4.1 Screen Readers</h4>
  <p>Our website is compatible with popular screen readers including:</p>
  <ul>
    <li><strong>NVDA</strong> (Windows)</li>
    <li><strong>JAWS</strong> (Windows)</li>
    <li><strong>VoiceOver</strong> (macOS, iOS)</li>
    <li><strong>TalkBack</strong> (Android)</li>
    <li><strong>Orca</strong> (Linux)</li>
  </ul>
  
  <h4>4.2 Browser Compatibility</h4>
  <p>Our website supports accessibility features in:</p>
  <ul>
    <li>Chrome (latest 2 versions)</li>
    <li>Firefox (latest 2 versions)</li>
    <li>Safari (latest 2 versions)</li>
    <li>Edge (latest 2 versions)</li>
  </ul>
  
  <h4>4.3 Mobile Accessibility</h4>
  <p>Our mobile application includes:</p>
  <ul>
    <li>VoiceOver support (iOS)</li>
    <li>TalkBack support (Android)</li>
    <li>Large text support</li>
    <li>High contrast mode</li>
    <li>Voice control compatibility</li>
  </ul>
  
  <h3>5. Keyboard Navigation</h3>
  
  <h4>5.1 Keyboard Shortcuts</h4>
  <p>The following keyboard shortcuts are available throughout the site:</p>
  <table>
    <tr>
      <th>Key Combination</th>
      <th>Action</th>
    </tr>
    <tr>
      <td>Tab</td>
      <td>Move to next interactive element</td>
    </tr>
    <tr>
      <td>Shift + Tab</td>
      <td>Move to previous interactive element</td>
    </tr>
    <tr>
      <td>Enter or Space</td>
      <td>Activate button or link</td>
    </tr>
    <tr>
      <td>Escape</td>
      <td>Close modal or dropdown</td>
    </tr>
    <tr>
      <td>Arrow Keys</td>
      <td>Navigate within menus and lists</td>
    </tr>
    <tr>
      <td>Alt + S</td>
      <td>Skip to main content</td>
    </tr>
  </table>
  
  <h3>6. Feedback and Support</h3>
  
  <h4>6.1 Accessibility Feedback</h4>
  <p>We welcome your feedback on the accessibility of VELOX Network. Please let us know if you encounter accessibility barriers:</p>
  <ul>
    <li><strong>Email:</strong> accessibility@velox-network.com</li>
    <li><strong>Phone:</strong> +44 (0) 7554012728 </li>
    <li><strong>Contact Form:</strong> Available on our contact page</li>
    <li><strong>Response Time:</strong> We aim to respond within 2 business days</li>
  </ul>
  
  <h4>6.2 What to Include in Your Feedback</h4>
  <p>When reporting accessibility issues, please include:</p>
  <ul>
    <li>The specific page or feature where you encountered the issue</li>
    <li>A description of the problem</li>
    <li>The assistive technology you were using (if any)</li>
    <li>Your browser and operating system</li>
    <li>Any error messages you received</li>
  </ul>
  
  <h3>7. Legal Framework</h3>
  
  <h4>7.1 Applicable Laws and Standards</h4>
  <p>Our accessibility efforts comply with:</p>
  <ul>
    <li><strong>UK Equality Act 2010</strong></li>
    <li><strong>EU Web Accessibility Directive (2016/2102)</strong></li>
    <li><strong>Americans with Disabilities Act (ADA)</strong> - for US users</li>
    <li><strong>WCAG 2.1 Level AA</strong></li>
    <li><strong>EN 301 549</strong> - European accessibility standard</li>
  </ul>
  
  <h3>8. Contact Information</h3>
  <p>For accessibility-related questions, feedback, or support:</p>
  <ul>
    <li><strong>Accessibility Team:</strong> accessibility@velox-network.com</li>
    <li><strong>General Support:</strong> support@velox-network.com</li>
    <li><strong>Phone:</strong> +44 (0) 7554012728 </li>
    <li><strong>Address:</strong> VELOX Network Ltd, London, UK</li>
  </ul>
  
  <p><em>We are committed to providing an accessible and inclusive experience for all users. Thank you for helping us improve accessibility on VELOX Network.</em></p>
`;

/**
 * The four legal documents, in tab order.
 *
 * Each has its own URL (`/policy/<id>`) so a footer entry, a signup form or an
 * email can point at one document directly instead of landing every reader on
 * Privacy and expecting them to find the rest.
 */
const DOCUMENTS = [
  {
    id: 'privacy',
    label: 'Privacy Policy',
    title: 'Privacy Policy',
    description:
      'How Velox Network collects, uses, shares and protects your personal data, and the rights you have under UK GDPR.',
    content: privacyPolicyContent,
  },
  {
    id: 'terms',
    label: 'Terms of Service',
    title: 'Terms of Service',
    description:
      'The terms governing your use of the Velox Network digital business card platform.',
    content: termsOfServiceContent,
  },
  {
    id: 'cookies',
    label: 'Cookie Policy',
    title: 'Cookie Policy',
    description:
      'The cookies Velox Network sets, what each one does, and how to change your preferences.',
    content: cookiePolicyContent,
  },
  {
    id: 'accessibility',
    label: 'Accessibility',
    title: 'Accessibility Statement',
    description:
      'Our commitment to WCAG 2.1 Level AA, the accessibility features we provide, and how to report a barrier.',
    content: accessibilityContent,
  },
];

/**
 * Wrap tables so they can scroll sideways instead of being clipped.
 *
 * The cookie and keyboard-shortcut tables are four and two columns wide and do
 * not fit a phone. The content is injected as a single HTML string, so there is
 * no element to wrap in JSX; the wrapper is added to the markup instead. Giving
 * the table its own scroll container keeps the rest of the document in normal
 * flow, which `overflow-x` on the content card would not.
 */
function withScrollableTables(html) {
  return html
    .replace(/<table>/g, '<div class="policy-table-wrap"><table>')
    .replace(/<\/table>/g, '</table></div>');
}

/**
 * Whether /policy should render with the public marketing chrome.
 *
 * Read synchronously from the stored token rather than from App's async
 * `isAuthenticated`, so that App's layout classes and the chrome rendered here
 * agree on the first paint instead of flashing a marketing header at a
 * signed-in reader. This only ever selects chrome: the documents are public
 * either way, so there is nothing here to protect.
 */
export function usesPublicChrome() {
  return !localStorage.getItem('token');
}

const PolicyPage = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  // An unrecognised section falls back to Privacy rather than erroring. These
  // URLs end up pasted into emails and contracts, so a readable document beats
  // a dead end.
  const active = DOCUMENTS.find((doc) => doc.id === section) || DOCUMENTS[0];

  const isPublic = usesPublicChrome();

  useDocumentMeta({
    title: active.title,
    description: active.description,
    path: `/policy/${active.id}`,
  });

  const handleBackClick = () => {
    setIsNavigating(true);
    navigate('/account');
  };

  const page = (
    <div className="policy-page">
      {/* Header Section. The H1 stays the collection rather than the active
          document, so the document's own H2 keeps the heading order intact. */}
      <div className="policy-header">
        {isPublic && <div className="vxm-grid-motif" />}
        <div className="policy-header-inner">
          <p className="policy-eyebrow">Legal</p>
          <h1>Legal Information</h1>
          <p>
            The policies governing your use of Velox, how we handle your data,
            and the rights you have.
          </p>
        </div>
      </div>

      {/* Main Container */}
      <div className="policy-container">
        {/* Links rather than buttons: each document is a real address that can
            be shared, bookmarked, opened in a new tab and crawled. */}
        <nav className="policy-tabs" aria-label="Legal documents">
          {DOCUMENTS.map((doc) => (
            <Link
              key={doc.id}
              to={`/policy/${doc.id}`}
              className={doc.id === active.id ? 'active' : ''}
              aria-current={doc.id === active.id ? 'page' : undefined}
            >
              {doc.label}
            </Link>
          ))}
        </nav>

        <div className="policy-content">
          <div
            dangerouslySetInnerHTML={{ __html: withScrollableTables(active.content) }}
          ></div>
        </div>

        {/* Back to the account hub only makes sense for a signed-in reader;
            everyone else navigates with the marketing header and footer. */}
        {!isPublic && !isNavigating && (
          <button
            className="back-to-contacts-button"
            onClick={handleBackClick}
            aria-label="Back to account"
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  return isPublic ? <MarketingLayout>{page}</MarketingLayout> : page;
};

export default PolicyPage;
